import React, { useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';

import { toast, confirm } from '@rickylandino/react-messages';
import NumberFormat from 'react-number-format';
import Moment from 'moment';

import Card from 'react-bootstrap/Card';
import '../../../css/card.css';

import { Empty, message, Select, Badge, Tooltip } from 'antd';

import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit';

import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

import ReactLoading from 'react-loading';

import { useForm, Controller } from "react-hook-form";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

import { Modal } from 'react-bootstrap';
import Axios from '../../../config/axios';

const { Option } = Select;

function ViewORUApiLogs(props) {

    const [state, setState] = useState({
        logs: [],
        loaded: false,
        pagination: {
            currentPage: 1,
            pageSize: 50,
            totalRecords: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false
        },
        filters: {}
    });

    const [filterOptions, setFilterOptions] = useState({
        statusCodes: [],
        reportStates: [],
        dateRange: {},
        sortOptions: []
    });

    const [selectedStatusCodes, setSelectedStatusCodes] = useState([]);
    const [selectedReportStates, setSelectedReportStates] = useState([]);
    const [hasErrors, setHasErrors] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('desc');

    const [loading, setLoading] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedLogDetail, setSelectedLogDetail] = useState(null);

    const { register, handleSubmit, setValue, getValues, watch, control } = useForm();

    const dateFrom = watch("dateFrom");
    const dateTo = watch("dateTo");

    useEffect(() => {
        loadFilterOptions();
        // Set default date range (last 7 days)
        const defaultDateTo = new Date();
        const defaultDateFrom = new Date();
        defaultDateFrom.setDate(defaultDateFrom.getDate() - 7);
        
        setValue('dateFrom', defaultDateFrom);
        setValue('dateTo', defaultDateTo);
        
        loadLogs(1); // Load first page
    }, []);

    const loadFilterOptions = async () => {
        try {
            const response = await Axios.get('/oru/api/ORUApiLog/filters');
            setFilterOptions(response.data);
        } catch (error) {
            console.error('Error loading filter options:', error);
            toast.error('Failed to load filter options');
        }
    };

    const loadLogs = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page: page,
                pageSize: state.pagination.pageSize,
                search: searchTerm || undefined,
                statusCode: selectedStatusCodes.length === 1 ? selectedStatusCodes[0] : undefined,
                reportState: selectedReportStates.length === 1 ? selectedReportStates[0] : undefined,
                dateFrom: dateFrom ? Moment(dateFrom).format('YYYY-MM-DD') : undefined,
                dateTo: dateTo ? Moment(dateTo).format('YYYY-MM-DD') : undefined,
                hasErrors: hasErrors,
                sortBy: sortBy,
                sortDirection: sortDirection
            };

            // Remove undefined values
            Object.keys(params).forEach(key => 
                params[key] === undefined && delete params[key]
            );

            const response = await Axios.get('/oru/api/ORUApiLog/logs', { params });
            
            setState(prevState => ({
                ...prevState,
                logs: response.data.data,
                pagination: response.data.pagination,
                filters: response.data.filters,
                loaded: true
            }));
        } catch (error) {
            console.error('Error loading logs:', error);
            toast.error('Failed to load ORU API logs');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        loadLogs(1);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedStatusCodes([]);
        setSelectedReportStates([]);
        setHasErrors(null);
        setSortBy('timestamp');
        setSortDirection('desc');
        setValue('dateFrom', null);
        setValue('dateTo', null);
        
        // Load logs with cleared filters
        setTimeout(() => loadLogs(1), 100);
    };

    const viewLogDetails = async (logId) => {
        try {
            const response = await Axios.get(`/oru/api/ORUApiLog/logs/${logId}`);
            setSelectedLogDetail(response.data);
            setShowDetailModal(true);
        } catch (error) {
            console.error('Error loading log details:', error);
            toast.error('Failed to load log details');
        }
    };

    const exportLogs = async () => {
        const key = "loading";
        message.loading({ content: 'Exporting ORU API Logs...', key, duration: 0 });
        
        try {
            // For now, we'll export the current page data
            // In a real implementation, you might want to export all filtered data
            const fileName = `oru-api-logs_${Moment().format('YYYYMMDDHm')}.json`;
            const dataStr = JSON.stringify(state.logs, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            
            const url = window.URL.createObjectURL(dataBlob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
            
            message.destroy(key);
        } catch (error) {
            message.destroy(key);
            toast.error('Failed to export logs');
        }
    };

    // Status code formatter with color coding
    const statusCodeFormatter = (cell, row) => {
        let color = 'default';
        if (cell >= 200 && cell < 300) color = 'success';
        else if (cell >= 300 && cell < 400) color = 'warning';
        else if (cell >= 400) color = 'error';
        
        return <Badge status={color} text={cell} />;
    };

    // Date formatter
    const dateFormatter = (cell) => {
        return cell ? Moment(cell).format('MM/DD/YYYY HH:mm:ss') : '';
    };

    // Action formatter for view details
    const actionFormatter = (cell, row) => {
        return (
            <Tooltip title="View Details">
                <i 
                    className="fas fa-eye fa-125x color-pink hover" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => viewLogDetails(row.ORUApiLogID)}
                />
            </Tooltip>
        );
    };

    // Request body preview formatter
    const requestBodyFormatter = (cell) => {
        if (!cell) return '';
        return (
            <Tooltip title={cell}>
                <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                    {cell}
                </span>
            </Tooltip>
        );
    };

    // Execution time formatter
    const executionTimeFormatter = (cell) => {
        if (!cell) return '';
        const timeMs = parseInt(cell);
        if (timeMs > 5000) return <span style={{ color: 'red' }}>{timeMs}ms</span>;
        if (timeMs > 2000) return <span style={{ color: 'orange' }}>{timeMs}ms</span>;
        return <span>{timeMs}ms</span>;
    };

    const columns = [
        {
            dataField: 'Timestamp',
            text: 'Timestamp',
            formatter: dateFormatter,
            sort: true,
        },
        {
            dataField: 'AccessionNumber',
            text: 'Accession #',
            sort: true,
        },
        {
            dataField: 'StatusCode',
            text: 'Status',
            formatter: statusCodeFormatter,
            sort: true,
            headerStyle: { width: '80px' }
        },
        {
            dataField: 'ReportState',
            text: 'State',
            headerStyle: { width: '60px' }
        },
        {
            dataField: 'ExecutionTimeMs',
            text: 'Time (ms)',
            formatter: executionTimeFormatter,
            headerStyle: { width: '80px' }
        },
        {
            dataField: 'ResponseMessage',
            text: 'Response Message',
        },
        {
            dataField: 'actions',
            text: 'Actions',
            isDummyField: true,
            formatter: actionFormatter,
            headerAlign: 'center',
            align: 'center',
            headerStyle: { width: '80px' }
        }
    ];

    const selectRow = {
        mode: 'radio',
        clickToSelect: true,
        hideSelectColumn: true
    };

    const sizePerPageOptionRenderer = ({
        text,
        page,
        onSizePerPageChange
    }) => (
        <li
            key={text}
            role="presentation"
            className="form-control-custom hover"
            tabIndex="-1"
            data-page={page}
            onMouseDown={(e) => {
                e.preventDefault();
                onSizePerPageChange(page);
            }}
            style={{ color: 'pink' }}
        >
            <span className="a hover"
                tabIndex="-1"
                role="menuitem"
                data-page={page}
                onMouseDown={(e) => {
                    e.preventDefault();
                    onSizePerPageChange(page);
                }}
                style={{ color: 'rgba(243, 0, 121, 1)' }}
            >
                {text}
            </span>
        </li>
    );

    const pagination = {
        page: state.pagination.currentPage,
        sizePerPage: state.pagination.pageSize,
        totalSize: state.pagination.totalRecords,
        showTotal: (start, to, total) => `Showing ${start} to ${to} of ${total} entries`,
        sizePerPageList: [
            { text: "25", value: 25 }, 
            { text: "50", value: 50 }, 
            { text: "100", value: 100 }
        ],
        sizePerPageOptionRenderer
    };

    // Handle table changes for remote pagination, sorting, etc.
    const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder, searchText }) => {
        let newPage = page;
        let newPageSize = sizePerPage;
        let newSortBy = sortBy;
        let newSortDirection = sortDirection;

        if (type === 'pagination') {
            newPage = page;
            newPageSize = sizePerPage || state.pagination.pageSize;
        } else if (type === 'sort') {
            newSortBy = sortField?.toLowerCase() || 'timestamp';
            newSortDirection = sortOrder?.toLowerCase() || 'desc';
        }

        // Update state and reload data
        setState(prevState => ({
            ...prevState,
            pagination: { ...prevState.pagination, pageSize: newPageSize }
        }));

        if (type === 'sort') {
            setSortBy(newSortBy);
            setSortDirection(newSortDirection);
        }

        loadLogs(newPage);
    };

    return (
        <div>
            {/*<div className="panel-hdr justify-content-end">*/}
            {/*    <button type="button" className="pinkTextA btn btn-outline-default margin-0-10" onClick={exportLogs}>*/}
            {/*        Export Logs*/}
            {/*    </button>*/}
            {/*</div>*/}

            <div className="row m-4">
                {/*<div className="col-12">*/}
                {/*    <p>Search Criteria</p>*/}
                {/*</div>*/}

                <div className="form-group col-lg-3">
                    <label className="form-label">Search</label>
                    <input 
                        className="form-control-custom" 
                        placeholder="Accession, Response, etc..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="form-group col-lg-2">
                    <label className="form-label">Error Filter</label>
                    <Select
                        onChange={setHasErrors}
                        allowClear
                        placeholder="All logs"
                        className="form-control-custom w-100"
                        bordered={false}
                        value={hasErrors}
                    >
                        <Option value={true}>Errors Only</Option>
                        <Option value={false}>Success Only</Option>
                    </Select>
                </div>

                <div className="form-group col-lg-3">
                    <label className="form-label">Date Range (From)</label>
                    <Controller
                        control={control}
                        name='dateFrom'
                        render={({ field }) => (
                            <DatePicker
                                placeholderText='From date'
                                onChange={(date) => setValue('dateFrom', date)}
                                selected={dateFrom || null}
                                selectsStart
                                startDate={dateFrom}
                                endDate={dateTo}
                                dateFormat="MM/dd/yyyy"
                                className="form-control-custom"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                            />
                        )}
                    />
                </div>
                <div className="form-group col-lg-3">
                    <label className="form-label">Date Range (To)</label>
                    <Controller
                        control={control}
                        name='dateTo'
                        render={({ field }) => (
                            <DatePicker
                                placeholderText='To date'
                                onChange={(date) => setValue('dateTo', date)}
                                selected={dateTo || null}
                                selectsEnd
                                startDate={dateFrom}
                                endDate={dateTo}
                                dateFormat="MM/dd/yyyy"
                                className="form-control-custom"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                            />
                        )}
                    />
                </div>

                <div className="form-group col-12 text-center">
                    <button type="button" className="btn btn-submit mr-3" onClick={handleSearch}>
                        Search
                    </button>
                    <button type="button" className="btn btn-outline-default" onClick={handleClearFilters}>
                        Clear Filters
                    </button>
                </div>
            </div>

            <Card className="pinkCard">
                <Card.Header>
                    <span>ORU API Logs</span>
                    {state.pagination.totalRecords > 0 && (
                        <span className="ml-3 text-muted">
                            ({state.pagination.totalRecords} total records)
                        </span>
                    )}
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="loading m-5 text-center d-flex justify-content-center">
                            <span className="frame-heading">
                                <ReactLoading className='tableLoading' type={"spokes"} color={'#F30079'} height={30} width={30} /> 
                                Loading logs...
                            </span>
                        </div>
                    ) : (
                        <div className="fullTable">
                            {state.loaded && state.logs.length > 0 ? (
                                <BootstrapTable
                                    keyField='ORUApiLogID'
                                    data={state.logs}
                                    columns={columns}
                                    pagination={paginationFactory(pagination)}
                                    selectRow={selectRow}
                                    hover
                                    condensed
                                    remote={{
                                        pagination: true,
                                        filter: false,
                                        sort: true
                                    }}
                                    onTableChange={handleTableChange}
                                />
                            ) : (
                                <Empty 
                                    image={Empty.PRESENTED_IMAGE_SIMPLE} 
                                    description={state.loaded ? "No logs found with current filters." : "Use the search criteria above to load logs."} 
                                />
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Log Detail Modal */}
            <Modal size='xl' show={showDetailModal} backdrop='static' onHide={() => setShowDetailModal(false)}>
                <Modal.Header>
                    <h1>ORU API Log Details</h1>
                    <button type="button" className="close" onClick={() => setShowDetailModal(false)}>
                        <span>&times;</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    {selectedLogDetail && (
                        <div className="panel-content">
                            <div className="row">
                                <div className="col-md-6">
                                    <h5>Basic Information</h5>
                                    <table className="table table-sm">
                                        <tbody>
                                            <tr><td><strong>Timestamp:</strong></td><td>{dateFormatter(selectedLogDetail.timestamp)}</td></tr>
                                            <tr><td><strong>Accession Number:</strong></td><td>{selectedLogDetail.accessionNumber}</td></tr>
                                            <tr><td><strong>Status Code:</strong></td><td>{statusCodeFormatter(selectedLogDetail.statusCode)}</td></tr>
                                            <tr><td><strong>Report State:</strong></td><td>{selectedLogDetail.reportState}</td></tr>
                                            <tr><td><strong>Execution Time:</strong></td><td>{executionTimeFormatter(selectedLogDetail.executionTimeMs)}</td></tr>
                                            <tr><td><strong>Correlation ID:</strong></td><td>{selectedLogDetail.correlationId}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="col-md-6">
                                    <h5>Response</h5>
                                    <div className="border p-2" style={{ minHeight: '100px', backgroundColor: '#f8f9fa' }}>
                                        {selectedLogDetail.responseMessage}
                                    </div>
                                    
                                    {selectedLogDetail.errorDetails && (
                                        <>
                                            <h5 className="mt-3">Error Details</h5>
                                            <div className="border p-2" style={{ minHeight: '100px', backgroundColor: '#fff5f5', color: 'red' }}>
                                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                                                    {selectedLogDetail.errorDetails}
                                                </pre>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            {selectedLogDetail.requestBody && (
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <h5>Request Body (JSON)</h5>
                                        <div className="border p-2" style={{ maxHeight: '400px', overflow: 'auto', backgroundColor: '#f8f9fa' }}>
                                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                                                {JSON.stringify(JSON.parse(selectedLogDetail.requestBody), null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-outline-default" onClick={() => setShowDetailModal(false)}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default withRouter(ViewORUApiLogs);